import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { useSelectedYear } from "$/lib/hooks/useSelectedYear";
import { useAuth } from "$/lib/providers/AuthProvider";
import type { DetailedAccountType } from "$/lib/types/models/Account.types";
import type {
  FinancialReportType,
  MissingDataResponse,
  ProfileCompletionData,
} from "$/lib/types/models/User.types";
import { ROUTES } from "$/lib/utils/constants/routes.constants";
import { restApiClient } from "$/lib/utils/restApiClient";

const apiEndPoint = "/users";

export async function assignAccountIdToUser(accountId: string) {
  return restApiClient.post(`${apiEndPoint}/assign-account`, {
    data: {
      accountId,
    },
  });
}

export function useAssignAccountIdToUser() {
  const { invalidateUser } = useAuth();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: assignAccountIdToUser,
    onSuccess: () => {
      invalidateUser();
      navigate({ to: ROUTES.HOMEPAGE });
    },
  });
}

export async function deleteUser(): Promise<number> {
  return restApiClient.post(`${apiEndPoint}/delete-user`);
}

export function useDeleteUser() {
  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      window.location.reload();
    },
  });
}

export async function chooseActiveAccountForUser(accountId: string) {
  return restApiClient.post(`${apiEndPoint}/active-account`, {
    data: {
      accountId,
    },
  });
}
export async function disconnectAccount() {
  return restApiClient.post(`${apiEndPoint}/disconnect-account`);
}

export async function switchActiveBridgeItem(bridgeItemId: number) {
  return restApiClient.post(`${apiEndPoint}/switch-bridge-item`, {
    data: {
      bridgeItemId,
    },
  });
}

export function useSwitchActiveBridgeItem() {
  const queryClient = useQueryClient();
  const { invalidateUser } = useAuth();

  return useMutation({
    mutationFn: switchActiveBridgeItem,
    onSuccess: () => {
      invalidateUser();
      queryClient.refetchQueries();
    },
  });
}

export function useChooseActiveAccountForUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: chooseActiveAccountForUser,
    onSuccess: () => {
      queryClient.refetchQueries();
      toast.success(
        "Opération réussie ! L'actualisation de vos transactions peut prendre quelques minutes.",
      );
    },
  });
}

export function useDisconnectAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: disconnectAccount,
    onSuccess: () => {
      queryClient.refetchQueries();
    },
  });
}

export async function getUserAccounts() {
  return restApiClient.get<DetailedAccountType[]>(`${apiEndPoint}/accounts`);
}

export function useGetUserAccounts() {
  return useQuery({
    queryKey: ["getUserAccounts"],
    queryFn: () => getUserAccounts(),
  });
}

export async function getProfileCompletion(year?: number) {
  return restApiClient.get<ProfileCompletionData>(
    `${apiEndPoint}/profile-completion`,
    {
      query: year ? { year } : undefined,
    },
  );
}

type UseGetProfileCompletionOptions = {
  enabled?: boolean;
};

export function useGetProfileCompletion(
  options?: UseGetProfileCompletionOptions,
) {
  const { sessionStorageYearValue } = useSelectedYear();

  return useQuery({
    queryKey: ["getProfileCompletion"],
    queryFn: () => getProfileCompletion(sessionStorageYearValue),
    ...options,
  });
}

type UseGetFinancialReportOptions = {
  enabled?: boolean;
  dataMode?: "estimate" | "real";
  regulariation: boolean;
};

export async function getFinancialReport(
  // dataMode: "estimate" | "real" = "real",
  options?: UseGetFinancialReportOptions,
): Promise<FinancialReportType> {
  return restApiClient.get(`${apiEndPoint}/financial-report`, {
    query: {
      dataMode: options?.dataMode || "real",
      regularisation: options?.regulariation || undefined,
    },
  });
}

export function useGetFinancialReport(options?: UseGetFinancialReportOptions) {
  return useQuery({
    queryKey: ["getFinancialReport", options],
    queryFn: () => getFinancialReport(options),
    ...options,
  });
}

export async function getMissingDataStatus(year?: number) {
  return restApiClient.get<MissingDataResponse>(`${apiEndPoint}/missing-data`, {
    query: year ? { year } : undefined,
  });
}

export function useGetMissingDataStatus() {
  const { sessionStorageYearValue } = useSelectedYear();

  return useQuery({
    queryKey: ["getMissingDataStatus", sessionStorageYearValue],
    queryFn: () => getMissingDataStatus(sessionStorageYearValue),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });
}

export async function updateOnboarding(key: string) {
  return restApiClient.patch(`${apiEndPoint}/onboarding`, {
    data: { key },
  });
}

export async function dismissCsvNotification() {
  return restApiClient.post(`${apiEndPoint}/dismiss-csv-notification`);
}

export function useDismissCsvNotification() {
  const { invalidateUser } = useAuth();

  return useMutation({
    mutationFn: dismissCsvNotification,
    onSuccess: () => {
      invalidateUser();
    },
  });
}

export type SCANotification = {
  type: "sca_reconnect";
  bridgeItemId: number;
  providerName: string;
  title: string;
  severity: "warning";
};

export async function getSCANotifications(): Promise<SCANotification[]> {
  return restApiClient.get(`${apiEndPoint}/notifications/sca`);
}

export function useGetSCANotifications() {
  return useQuery({
    queryKey: ["getSCANotifications"],
    queryFn: () => getSCANotifications(),
  });
}
